import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  cleanAccidentForm,
  getAccidentFacade,
  getAccidentForm,
  getAccidents,
} from "../../../../actions/accidents";
import { getFormDiagnosis, getFormEmployers } from "../../../../actions/forms";
import snackbarUtils from "../../../../utils/snackbarUtils";
import AccidentFormContainer from "../../forms/accident/accidentFormContainer";
import AccidentsFacadeAndOpen from "../newAccident/facadeAndOpenAccidents/accidentsFacadeAndOpen";
import EditAccidentFormContainer from "./editAccidentFormContainer";

const EditAccidentContainer = ({ accident, handleClose, propsAction }) => {
  const dispatch = useDispatch();

  const facadeState = useSelector((state) => state.accidents.facade);
  const roles = useSelector((state) => state.auth.roles);

  const [artId, setArtId] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [openFullForm, setOpenFullForm] = useState(false);
  const [facadeAccidents, setFacadeAccidents] = useState([]);
  const [openFacade, setOpenFacade] = useState(false);
  const [facadeAccident, setFacadeAccident] = useState(null);
  const [siniestroId, setSiniestroId] = useState(null);

  const esPrestador = roles?.PRESTADOR;
  const esPerfilDeSoporte = roles?.tipo === "Soporte" || roles?.tipo === "Administrador";

  const handleSearch = (accident) => {
    setArtId(accident.mlCloudCoreArtId);
    setSiniestroId(accident.idSiniestro);

    // Si es prestador, usar formulario completo para ambos tipos F y M
    if (esPrestador) {
      dispatch(
        getAccidentFacade(
          accident.mlCloudCoreArtId,
          accident.tipoDocumento,
          accident.documento,
          accident.prestadorId,
          accident.idSiniestro,
          true, // validateSiniestro
        ),
      ).then((res) => {
        handleOpenFacadeForPrestador(res.value.data, accident.mlCloudCoreArtId);
      });
      return;
    }

    // Si es soporte/admin, mantener lógica actual F/M
    switch (accident.metodoCarga) {
      case "M":
        dispatch(
          getAccidentForm(
            accident.mlCloudCoreArtId,
            accident.tipoDocumento,
            accident.documento,
            accident.idSiniestro,
            null,
          ),
        );
        dispatch(getFormDiagnosis(accident.mlCloudCoreArtId));
        setOpenForm(true);
        break;
      case "F":
        dispatch(
          getAccidentFacade(
            accident.mlCloudCoreArtId,
            accident.tipoDocumento,
            accident.documento,
            accident.prestadorId,
            accident.idSiniestro,
            !esPerfilDeSoporte,
          ),
        ).then((res) => {
          handleOpenFacade(res.value.data, accident.mlCloudCoreArtId);
        });
        setOpenFacade(true);
        break;
      default:
        snackbarUtils.error("No es posible modificar el siniestro.");
    }
  };

  // Handler para prestadores: abre formulario completo directamente
  const handleOpenFacadeForPrestador = (facadeAccidents, artId) => {
    if (facadeAccidents.length === 1) {
      handleEditFacadeForPrestador(facadeAccidents[0], artId);
    } else if (facadeAccidents.length > 1) {
      // Si hay más de un siniestro, mostrar selector
      setFacadeAccidents(facadeAccidents);
      setOpenFacade(true);
    } else {
      snackbarUtils.error("No se encontraron datos del siniestro.");
    }
  };

  const handleEditFacadeForPrestador = (facadeAccident, artIdParam) => {
    const artIdAux = artIdParam || artId;
    setFacadeAccident({ ...facadeAccident, siniestro: accident.idSiniestro });
    dispatch(
      getAccidentForm(
        artIdAux,
        null,
        null,
        null,
        facadeAccident.diagnosticoOMS,
      ),
    );
    dispatch(getFormDiagnosis(artIdAux));
    dispatch(getFormEmployers(artIdAux));
    setOpenFullForm(true);
    setOpenFacade(false);
  };

  // Handler para soporte/admin: mantiene lógica actual
  const handleOpenFacade = (facadeAccidents, artId) => {
    if (facadeAccidents.length === 1) {
      handleEditFacade(facadeAccidents[0], artId);
    } else {
      setFacadeAccidents(facadeAccidents);
    }
  };

  const handleEditFacade = (facadeAccident, artIdParam) => {
    const artIdAux = artIdParam || artId;
    setFacadeAccident({ ...facadeAccident, siniestro: accident.idSiniestro });
    dispatch(
      getAccidentForm(
        artIdAux,
        null,
        null,
        null,
        facadeAccident.diagnosticoOMS,
      ),
    );
    dispatch(getFormDiagnosis(artIdAux));
    setOpenForm(true);
    handleCloseFacade();
  };

  const handleCloseFacade = () => {
    setOpenFacade(false);
    setFacadeAccidents([]);
    handleClose();
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setFacadeAccident(null);
    dispatch(cleanAccidentForm());
    handleClose();
  };

  const handleCloseFullForm = () => {
    setOpenFullForm(false);
    setFacadeAccident(null);
    dispatch(cleanAccidentForm());
    handleClose();
  };

  const handleSubmitForm = (ret) => {
    handleCloseFacade();
    handleCloseForm();
    handleCloseFullForm();
    if (propsAction) {
      propsAction(ret);
    } else {
      dispatch(getAccidents());
    }
    snackbarUtils.success("Siniestro modificado correctamente");
  };

  useEffect(() => {
    if (accident) {
      handleSearch(accident);
    }
  }, [accident]);

  return (
    <div>
      {/* Formulario completo para prestadores */}
      <AccidentFormContainer
        open={openFullForm}
        artId={artId}
        handleCloseForm={handleCloseFullForm}
        handleSubmitForm={handleSubmitForm}
        facadeAccident={facadeAccident}
        isEditMode={true}
        siniestroId={siniestroId}
      />
      {/* Formulario reducido para soporte/admin */}
      <EditAccidentFormContainer
        open={openForm}
        artId={artId}
        handleCloseForm={handleCloseForm}
        handleSubmitForm={handleSubmitForm}
        facadeAccident={facadeAccident}
      />
      <AccidentsFacadeAndOpen
        open={openFacade}
        handleClose={handleCloseFacade}
        openAccidents={[]}
        facadeAccidents={facadeAccidents}
        handleEdit={esPrestador ? handleEditFacadeForPrestador : handleEditFacade}
        loading={facadeState.isFetching}
      />
    </div>
  );
};

export default EditAccidentContainer;
